import React from "react";
import {
    Alert,
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    FormHelperText,
    InputLabel,
    MenuItem,
    Select,
    TextField
} from "@mui/material";
import AdminData from "../../backend/dataservices/AdminData";

/**
 * Legt ein lokales Konto an — bewusst ohne Passwort.
 *
 * Der lokale Login ist deaktiviert, solche Konten sind daher ausschliesslich
 * ueber eine User-Relation erreichbar und dienen dazu, einem Benutzer mit einem
 * einzigen Login mehrere Konten (etwa je Tenant) zur Verfuegung zu stellen.
 */
function AdminCreateUserDialog({ roles, tenants, onClose, onCreated }) {
    const [username, setUsername] = React.useState('');
    const [fullName, setFullName] = React.useState('');
    const [email, setEmail] = React.useState('');
    const [role, setRole] = React.useState('');
    const [tenantId, setTenantId] = React.useState('');
    const [saving, setSaving] = React.useState(false);
    const [error, setError] = React.useState(null);

    const needsDiszi = roles.find(r => r.name === role)?.disziScope === 'single';

    function handleSave() {
        setSaving(true);
        setError(null);
        AdminData.createUser({
            username: username.trim(),
            fullName: fullName.trim(),
            email: email.trim(),
            role: role || null,
            tenantId: tenantId === '' ? null : tenantId
        }).then(() => {
            onCreated();
            onClose();
        }).catch(err => {
            setError(err?.response?.data?.error || 'Fehler beim Anlegen des Kontos.');
            setSaving(false);
        });
    }

    return (
        <Dialog open onClose={onClose} maxWidth="xs" fullWidth>
            <DialogTitle>Lokales Konto anlegen</DialogTitle>
            <DialogContent>
                <Box sx={{pt: 1, display: 'flex', flexDirection: 'column', gap: 2}}>
                    <Alert severity="info">
                        Es wird kein Passwort vergeben. Das Konto kann sich nicht selbst
                        anmelden und ist nur über eine User-Relation erreichbar.
                    </Alert>

                    <TextField
                        label="Benutzername"
                        size="small"
                        fullWidth
                        required
                        value={username}
                        onChange={e => setUsername(e.target.value)}
                        helperText="Muss systemweit eindeutig sein."
                    />

                    <TextField
                        label="Vollständiger Name"
                        size="small"
                        fullWidth
                        value={fullName}
                        onChange={e => setFullName(e.target.value)}
                        placeholder="leer = Benutzername"
                    />

                    <TextField
                        label="E-Mail"
                        size="small"
                        fullWidth
                        value={email}
                        onChange={e => setEmail(e.target.value)}
                    />

                    <FormControl fullWidth size="small">
                        <InputLabel>Rolle</InputLabel>
                        <Select value={role} label="Rolle" onChange={e => setRole(e.target.value)}>
                            <MenuItem value=""><em>Keine Rolle</em></MenuItem>
                            {roles.map(r => (
                                <MenuItem key={r.name} value={r.name}>{r.name}</MenuItem>
                            ))}
                        </Select>
                        {needsDiszi && (
                            <FormHelperText>
                                Die Führungskraft wird anschließend über „Benutzer
                                bearbeiten“ zugeordnet. Bis dahin sieht das Konto keine
                                Mitarbeiter.
                            </FormHelperText>
                        )}
                    </FormControl>

                    <FormControl fullWidth size="small">
                        <InputLabel>Tenant</InputLabel>
                        <Select value={tenantId} label="Tenant" onChange={e => setTenantId(e.target.value)}>
                            <MenuItem value=""><em>Kein Tenant</em></MenuItem>
                            {tenants.map(t => (
                                <MenuItem key={t.T_ID} value={t.T_ID}>
                                    {t.T_Code} – {t.T_Name}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    {role && tenantId === '' && (
                        <Alert severity="warning">
                            Ohne Tenant können die Rechte der Rolle nicht gesetzt werden.
                            Das Konto kann dann noch nicht arbeiten.
                        </Alert>
                    )}

                    {error && <Alert severity="error">{error}</Alert>}
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} style={{color: 'gray'}}>Abbrechen</Button>
                <Button onClick={handleSave} color="primary" disabled={!username.trim() || saving}>
                    {saving ? <CircularProgress size={20}/> : 'Anlegen'}
                </Button>
            </DialogActions>
        </Dialog>
    );
}

export default AdminCreateUserDialog;
